import React, { useState, useEffect } from 'react';
import { adminReferralAPI } from '../../lib/api';
import { Settings, Save, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';

const AdminReferralSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await adminReferralAPI.getSettings();
      setSettings(res.data.settings);
    } catch { toast.error('Failed to load referral settings'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminReferralAPI.updateSettings(settings);
      toast.success('Referral settings updated!');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const update = (key, value) => setSettings({ ...settings, [key]: value });

  if (loading || !settings) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Referral Settings</h1>
          <p className="text-[#5F6368]">Configure commission rates, payout thresholds, and program rules</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-google flex items-center gap-2" data-testid="save-referral-settings-btn">
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Enable Toggle */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-[#202124]">Referral Program</h3>
            <p className="text-sm text-[#5F6368]">Enable or disable the referral program</p>
          </div>
          <button onClick={() => update('enabled', !settings.enabled)} className="flex items-center gap-2" data-testid="toggle-referral-program">
            {settings.enabled ? <ToggleRight className="w-10 h-10 text-[#1E8E3E]" /> : <ToggleLeft className="w-10 h-10 text-[#DADCE0]" />}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Commission Rates */}
        <div className="dashboard-card space-y-4">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Commission Rates</h3>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">One-Time Commission (%)</label>
            <input type="number" step="0.1" value={settings.one_time_commission_percent || 0} onChange={e => update('one_time_commission_percent', parseFloat(e.target.value) || 0)} className="input-google py-2 text-sm" data-testid="one-time-commission-input" />
            <p className="text-xs text-[#9AA0A6] mt-1">Earned when referred user makes first purchase</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Recurring Commission (%)</label>
            <input type="number" step="0.1" value={settings.recurring_commission_percent || 0} onChange={e => update('recurring_commission_percent', parseFloat(e.target.value) || 0)} className="input-google py-2 text-sm" data-testid="recurring-commission-input" />
            <p className="text-xs text-[#9AA0A6] mt-1">Earned on every renewal by referred user</p>
          </div>
        </div>

        {/* Payout Settings */}
        <div className="dashboard-card space-y-4">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Payout Rules</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Min Payout (INR)</label>
              <input type="number" value={settings.min_payout_inr || 0} onChange={e => update('min_payout_inr', parseFloat(e.target.value) || 0)} className="input-google py-2 text-sm" data-testid="min-payout-inr-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Min Payout (USD)</label>
              <input type="number" value={settings.min_payout_usd || 0} onChange={e => update('min_payout_usd', parseFloat(e.target.value) || 0)} className="input-google py-2 text-sm" data-testid="min-payout-usd-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Payout Cycle</label>
            <select value={settings.payout_cycle || 'monthly'} onChange={e => update('payout_cycle', e.target.value)} className="input-google py-2 text-sm" data-testid="payout-cycle-select">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>

        {/* Attribution */}
        <div className="dashboard-card space-y-4">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Attribution</h3>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Cookie Duration (Days)</label>
            <input type="number" value={settings.cookie_duration_days || 60} onChange={e => update('cookie_duration_days', parseInt(e.target.value) || 60)} className="input-google py-2 text-sm" data-testid="cookie-duration-input" />
            <p className="text-xs text-[#9AA0A6] mt-1">How long a referral link stays valid after click</p>
          </div>
          <div className="bg-[#F1F3F4] rounded-lg p-3 text-xs text-[#5F6368]">
            Attribution model: <strong>Last Click Wins</strong> — if a user clicks multiple referral links, the last one is credited.
          </div>
        </div>

        {/* Approval */}
        <div className="dashboard-card space-y-4">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Approval</h3>
          <div>
            <label className="block text-xs font-medium text-[#5F6368] mb-1">Approval Waiting Period (Days)</label>
            <input type="number" value={settings.approval_waiting_days || 7} onChange={e => update('approval_waiting_days', parseInt(e.target.value) || 7)} className="input-google py-2 text-sm" data-testid="approval-days-input" />
            <p className="text-xs text-[#9AA0A6] mt-1">Commission stays "Pending" for this many days (refund window) before admin can approve</p>
          </div>
        </div>

        {/* Badge Milestones */}
        <div className="dashboard-card space-y-4">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Badge Milestones</h3>
          <p className="text-xs text-[#9AA0A6]">Set the number of referrals needed to earn each badge tier</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Bronze (referrals)</label>
              <input type="number" value={settings.badge_bronze || 5} onChange={e => update('badge_bronze', parseInt(e.target.value) || 5)} className="input-google py-2 text-sm" data-testid="badge-bronze-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Silver (referrals)</label>
              <input type="number" value={settings.badge_silver || 15} onChange={e => update('badge_silver', parseInt(e.target.value) || 15)} className="input-google py-2 text-sm" data-testid="badge-silver-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Gold (referrals)</label>
              <input type="number" value={settings.badge_gold || 30} onChange={e => update('badge_gold', parseInt(e.target.value) || 30)} className="input-google py-2 text-sm" data-testid="badge-gold-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#5F6368] mb-1">Diamond (referrals)</label>
              <input type="number" value={settings.badge_diamond || 50} onChange={e => update('badge_diamond', parseInt(e.target.value) || 50)} className="input-google py-2 text-sm" data-testid="badge-diamond-input" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReferralSettingsPage;
